import React, { forwardRef, Suspense, useRef } from "react"
import { faShieldCheck } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { SUPPORT_ADMIN_HEALTH_SCHEDULER_ROUTE, SUPPORT_ADMIN_ROUTE } from "../../../../../../constants/routes"
import {
    editSchedulerJob,
    fetchSchedulerJobDetails,
    SchedulerJobByIdResponse,
    SupportSchedulerQuery,
} from "../schedulerApi"
import { useNavigate, useParams } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { FormikProps } from "formik"
import { CombinedJobScheduleInfo, EditSchedulerJobFormValues, JobStatus } from "../schedulerTypes"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { EditSchedulerJobForm } from "../components/EditSchedulerJobForm"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { DateTime } from "luxon"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"

export function EditSchedulerJob() {
    const schedulerJobFormRef = useRef<FormikProps<EditSchedulerJobFormValues>>(null)

    const handleSaveClick = () => {
        if (schedulerJobFormRef.current) {
            schedulerJobFormRef.current?.submitForm()
        }
    }

    return (
        <>
            <LocalizedStrict id="support-reports-health-schedule-edit-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPORT_ADMIN_SCHEDULER_EDIT_JOB}
                    title="Edit scheduler job"
                    icon={<FontAwesomeIcon icon={faShieldCheck} />}
                    rightElement={
                        <LocalizedStrict id="support-reports-health-schedule-edit-page-edit-button">
                            <StandardButton onClick={handleSaveClick}>Edit</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-health">
                            <Breadcrumb>Health</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-health-scheduler">
                            <BreadcrumbLink to={SUPPORT_ADMIN_HEALTH_SCHEDULER_ROUTE}>Scheduler</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-reports-health-schedule-edit-page-nav-item-edit-job">
                            <Breadcrumb>Edit Job</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <SchedulerJob ref={schedulerJobFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const selectJobDetails = (data: SchedulerJobByIdResponse): CombinedJobScheduleInfo | null => {
    const schedulerJob = data?.schedulerJob
    if (!schedulerJob) return null

    return {
        ...schedulerJob,
        startTime: DateTime.fromSQL(schedulerJob.startTime),
        status: schedulerJob.status === JobStatus.ACTIVE,
    }
}

const SchedulerJob = forwardRef<FormikProps<EditSchedulerJobFormValues>>((props, ref) => {
    const navigate = useNavigate()
    const { jobId } = useParams()

    const { data } = useApiQuery(fetchSchedulerJobDetails, {
        queryName: SupportSchedulerQuery.SCHEDULER_JOB_DETAILS,
        dependencies: {
            jobId: jobId || "",
        },
        enabled: Boolean(jobId),
        select: selectJobDetails,
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutate: callEditSchedulerJob } = useApiMutation(editSchedulerJob, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SupportSchedulerQuery.SCHEDULER_STATUS] })
            if ("serviceResponse" in data && data.serviceResponse.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse.displayMessage,
                })
            }
            navigate(SUPPORT_ADMIN_HEALTH_SCHEDULER_ROUTE)
        },
    })

    const onEditSchedulerJob = (data: EditSchedulerJobFormValues) => {
        if (!jobId) return

        callEditSchedulerJob({
            jobId: +jobId,
            body: {
                startTime: data.startTime.toFormat("yyyy-MM-dd HH:mm"),
                cronExpression: data.cronExpression,
                status: data.status ? JobStatus.ACTIVE : JobStatus.INACTIVE,
            },
        })
    }

    return <EditSchedulerJobForm ref={ref} initialValues={data} onSubmit={onEditSchedulerJob} />
})
